import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { X, Loader2 } from 'lucide-react';
import { CATEGORIES, STATUSES, DEPLOYMENT_STATUSES } from './constants';

const OptionRow = ({ label, options, selected, onSelect, cols = 'flex' }) => (
  <div>
    <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">{label}</label>
    <div className={cols === 'grid' ? 'grid grid-cols-2 gap-2' : 'flex gap-2'}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onSelect(opt)}
          className={`px-3 py-2 rounded-lg text-sm capitalize transition-all ${
            selected === opt
              ? 'bg-[#7dd3fc] text-[#1a1c1b]'
              : 'bg-[#1a1c1b] text-[#888680] border border-[#1e3a5f]'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

export const AppFormModal = ({ formData, setFormData, onSubmit, onClose, isSubmitting, editingApp }) => {
  const update = (patch) => setFormData({ ...formData, ...patch });

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#302f2c] border border-[#1e3a5f]/50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
        <div className="p-6 border-b border-[#1e3a5f]/50 flex items-center justify-between sticky top-0 bg-[#302f2c] z-10">
          <h2 className="text-[#7dd3fc] font-bold text-xl">
            {editingApp ? 'Edit App' : 'Add New App'}
          </h2>
          <button onClick={onClose} className="text-[#888680] hover:text-[#7dd3fc]">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">App Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="My App"
                data-testid="app-name-input"
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-11"
              />
            </div>
            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">Admin Dashboard URL *</label>
              <Input
                value={formData.admin_url}
                onChange={(e) => update({ admin_url: e.target.value })}
                placeholder="https://myapp.com/admin"
                data-testid="app-admin-url-input"
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-11"
              />
            </div>
          </div>

          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder="Brief description of the app..."
              rows={3}
              className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">Icon URL</label>
              <Input
                value={formData.icon_url}
                onChange={(e) => update({ icon_url: e.target.value })}
                placeholder="https://example.com/icon.png"
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-11"
              />
            </div>
            <div>
              <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">Deployment URL</label>
              <Input
                value={formData.deployment_url}
                onChange={(e) => update({ deployment_url: e.target.value })}
                placeholder="https://myapp.com"
                className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <OptionRow label="Category" options={CATEGORIES} selected={formData.category} onSelect={(category) => update({ category })} cols="grid" />
            <OptionRow label="Status" options={STATUSES} selected={formData.status} onSelect={(status) => update({ status })} />
            <OptionRow label="Deployment" options={DEPLOYMENT_STATUSES} selected={formData.deployment_status} onSelect={(deployment_status) => update({ deployment_status })} />
          </div>

          <div>
            <label className="text-[#7dd3fc] text-sm font-semibold mb-2 block">Notes</label>
            <Textarea
              value={formData.notes}
              onChange={(e) => update({ notes: e.target.value })}
              placeholder="Internal notes about this app..."
              rows={3}
              className="bg-[#1a1c1b] border-[#1e3a5f] text-[#7dd3fc] resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1 border-[#1e3a5f] text-[#888680] rounded-full py-6"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              data-testid="app-form-submit-btn"
              className="flex-1 bg-[#7dd3fc] text-[#1a1c1b] hover:bg-[#7dd3fc]/90 rounded-full py-6 font-semibold"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                editingApp ? 'Update App' : 'Add App'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
